import { Visibility } from '@mui/icons-material';
import {
  Alert,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../../forms';
import { Dispatch, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { VTextField } from '../../../../forms';
import getValidationErrors from '../../../../helpers/getValidationErrors';
import { Button } from '../../../MUI/button/Button';
import { IFormData } from '../LoginPage';
import { ILogin } from '../../../../services';

const loginSchema = Yup.object().shape({
  email: Yup.string().email(),
  phone: Yup.string().test('phone-ddd', 'Telefone deve conter DDD (ex: 51999999999)', (val) => {
    if (!val) return true; // campo vazio é validado no handleSubmit
    const digits = val.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 11;
  }),
  password: Yup.string().required().min(5),
});

interface IProps {
  errorMessage: string | undefined;
  setErrorMessage: Dispatch<React.SetStateAction<string | undefined>>;
  showPassword: boolean | undefined;
  setShowPassword: Dispatch<React.SetStateAction<boolean>>;
  mutateLogin: UseMutateFunction<string | void, any, ILogin, unknown>;
  isLoadingLogin: boolean;
}

export const Login: React.FC<IProps> = ({
  errorMessage,
  showPassword,
  setShowPassword,
  mutateLogin,
  isLoadingLogin,
}) => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const [usePhone, setUsePhone] = useState(false);

  const handleSubmit = useCallback(async (data: IFormData) => {
    try {
      formRef.current?.setErrors({});

      const loginPayload: ILogin = { password: data.password };

      if (usePhone) {
        if (!data.phone) {
          formRef.current?.setErrors({ phone: 'Informe o telefone' });
          return;
        }
        loginPayload.phone = data.phone.replace(/\D/g, '');
      } else {
        if (!data.email) {
          formRef.current?.setErrors({ email: 'Informe o e-mail' });
          return;
        }
        loginPayload.email = data.email;
      }

      await loginSchema.validate(
        { email: data.email, phone: data.phone, password: data.password },
        { abortEarly: false }
      );

      mutateLogin(loginPayload);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err as Yup.ValidationError);
        formRef.current?.setErrors(errors);
      }
    }
  }, [usePhone]);

  return (
    <Form ref={formRef} onSubmit={handleSubmit} id='login-form'>
      <Box display='flex' flexDirection='column' gap={2}>
        <Typography variant='h5' align='center'>
          Login
        </Typography>

        <Divider variant='middle' />

        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <ToggleButtonGroup
            value={usePhone ? 'phone' : 'email'}
            exclusive
            size='small'
            onChange={(_, value) => {
              if (value) {
                setUsePhone(value === 'phone');
                formRef.current?.setErrors({});
                formRef.current?.setFieldValue('email', '');
                formRef.current?.setFieldValue('phone', '');
              }
            }}
            fullWidth
          >
            <ToggleButton value='email' sx={{ flex: 1 }}>E-mail</ToggleButton>
            <ToggleButton value='phone' sx={{ flex: 1 }}>Telefone</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {usePhone ? (
          <VTextField
            fullWidth
            label='Telefone (com DDD)'
            name='phone'
            disabled={isLoadingLogin}
            placeholder='Ex: 51999999999'
          />
        ) : (
          <VTextField
            fullWidth
            label='E-mail'
            name='email'
            type='email'
            disabled={isLoadingLogin}
          />
        )}

        <VTextField
          fullWidth
          label='Senha'
          name='password'
          type={showPassword ? 'text' : 'password'}
          required
          disabled={isLoadingLogin}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Visibility />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box display='flex' justifyContent='left'>
          <Button
            label='Esqueceu sua senha?'
            variant='text'
            onClick={() => navigate('/forgot-password')}
            style={{ fontSize: 12 }}
          />
        </Box>
      </Box>
    </Form>
  );
};
